import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Typography,
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Button,
  Spin,
  Popover,
  Avatar,
} from "antd";
import {
  DownloadOutlined,
  LoadingOutlined,
  InfoCircleOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import store from "./store/store";
import {
  registryPlatformProjectsApi,
  useGetPlatformProjectDetailsQuery,
} from "./store/registryplatformprojectsapi";
import { RegistryDisplayOrganizationName } from "./RegistryDisplayOrgName";
import { registryDocumentApi } from "./store/registrydocumentapi";
import axios from "axios";
import moment from "moment";

function RegistryProjectDetailsPageView() {
  const { id } = useParams();
  const history = useHistory();

  const [isSpinning, setIsSpinning] = useState(false);

  const [methodData, setMethodData] = useState();
  const [documentList, setDocumentList] = useState();
  const [yieldDataList, setYieldDataList] = useState();
  const [retirementList, setRetirementList] = useState();
  const { data, isError, isLoading } = useGetPlatformProjectDetailsQuery(id, { skip: !id });

  const project = data?.data;

  const resolvedProject = project
    ? {
        _id: project._id,
        name: project.name,
        ghg_project_summary: project.ghg_project_summary,
        status: project.creditIssuanceRequired === false
          ? "Quantification Only"
          : project.state,
        organizationName: project.organizationName,
        standards_used: project.standardsUsed,
        project_period: project.projectPeriodStart && project.projectPeriodEnd
          ? [project.projectPeriodStart, project.projectPeriodEnd]
          : undefined,
        crediting_period: project.creditingPeriodStart && project.creditingPeriodEnd
          ? [project.creditingPeriodStart, project.creditingPeriodEnd]
          : undefined,
        methodologies: project.methodologies ?? [],
        type: project.type,
      }
    : undefined;

  const isReductionProject = resolvedProject?.type?.trim().toLowerCase() !== "removal";

  const handleGoBack = () => {
    history.push("/");
  };

  const fetchData = useCallback(async () => {
    try {
      setIsSpinning(true);

      const { data: creditsData } = await store.dispatch(
        registryPlatformProjectsApi.endpoints.getPlatformProjectCredits.initiate(id)
      );

      const docIds = [...new Set(project.documentIds)];
      var { data: documentListData } = await store.dispatch(
        registryDocumentApi.endpoints.getRegistryDocumentDetails.initiate({
          docids: JSON.stringify(docIds),
        })
      );

      // Filter out duplicate documents based on "doctype", "filename", and "hash"
      const uniqueDocumentList = documentListData?.reduce((acc, current) => {
        const isDuplicate = acc.some(
          (doc) =>
            doc.doctype === current.doctype &&
            doc.filename === current.filename &&
            doc.hash === current.hash
        );
        if (!isDuplicate) {
          acc.push(current);
        }
        return acc;
      }, []);

      // Update state with filtered list
      setDocumentList(uniqueDocumentList);
      setYieldDataList(creditsData?.data?.yields);
      setRetirementList(creditsData?.data?.retirements);

      setMethodData(project.methodologies ?? []);
    } catch {
    } finally {
      setIsSpinning(false);
    }
  }, [project, id]);

  const renderAssignedOrganization = (assigneeType) => {
    const assignment = project?.assignments?.find(
      (item) => item.assigneeType === assigneeType
    );
    if (!assignment) return null;
    return <RegistryDisplayOrganizationName name={assignment.organizationName} />;
  };

  const verifier = renderAssignedOrganization("Verifier");
  const validator = renderAssignedOrganization("Validator");
  const sponsor = renderAssignedOrganization("Project Sponsor");

  useEffect(() => {
    if (project) {
      fetchData();
    }
  }, [project, fetchData]);

  const onDownloadDocument = (record) => {
    axios
      .get("/ds/document/registrydocument/" + record.ID, {
        responseType: "blob",
      })
      .then((blob) => {
        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([blob.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", record.filename);

        // Append to html link element page
        document.body.appendChild(link);

        // Start download
        link.click();

        // Clean up and remove the link
        link.parentNode.removeChild(link);
      });
  };

  const format = (value) => (
    <div>
      {!isSpinning && (
        <Typography.Text style={{ fontSize: 18 }}>{value}</Typography.Text>
      )}
    </div>
  );

  const formatNumber = (value) => {
    if (value == null || value === 0) return "-";
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const YieldColumns = [
    {
      title: "Period",
      dataIndex: "period",
      key: "period",
      sorter: (a, b) => a.period.localeCompare(b.period),
      defaultSortOrder: "ascend",
      render: (record) => {
        return (
          <Typography.Text style={{ fontFamily: "Lexend", fontSize: 16 }}>
            {record}
          </Typography.Text>
        );
      },
    },

    {
      title: "Estimated Impact (tCo2e)",
      dataIndex: "co2Estimated",
      key: "co2Estimated",
      render: (record) => {
        return (
          <Typography.Text style={{ fontFamily: "Lexend", fontSize: 16 }}>
            {formatNumber(record)}
          </Typography.Text>
        );
      },
    },
    {
      title: (
        <span>
          Issued {isReductionProject ? "Inset Units" : "Credits"} (tCo2e)
          <Popover content={`Amount of issued ${isReductionProject ? "inset units" : "credits"} for this project per GHG yield period, expressed in total amount of tCO2e.`}>
            <InfoCircleOutlined style={{ marginLeft: 5 }} />
          </Popover>
        </span>
      ),
      dataIndex: "co2Realized",
      key: "co2Realized",
      render: (record) => {
        return (
          <Typography.Text style={{ fontFamily: "Lexend", fontSize: 16 }}>
            {formatNumber(record)}
          </Typography.Text>
        );
      },
    },
    {
      title: `Issued ${isReductionProject ? "Inset Unit" : "Credit"} IDs`,
      dataIndex: "issuedCredits",
      key: "credit_ids",
      width: 210,
      render: (issuedCredits) => {
        const yieldIds = issuedCredits.map((item) => `NFRY-${item.nfryId}`);

        // Display logic
        if (yieldIds.length === 0) {
          return (
            <Typography.Text style={{ fontFamily: "Lexend", fontSize: 16 }}>
              -
            </Typography.Text>
          );
        } else {
          return (
            <Popover
              content={
                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {issuedCredits.map(({ nfryId, co2Realized }) => {
                      const idText = `NFRY-${nfryId} containing ${co2Realized} credits within the range (NFRY-${nfryId}:1 - NFRY-${nfryId}:${co2Realized})`;
                      return (
                        <Typography.Text
                          key={nfryId}
                          style={{
                            display: "block",
                            fontFamily: "Lexend",
                            fontSize: 14,
                          }}
                        >
                          • {idText}
                        </Typography.Text>
                      );
                    })}
                </div>
              }
              title="All Credit IDs"
              trigger="click"
            >
              <Typography.Text
                style={{
                  fontFamily: "Lexend",
                  fontSize: 16,
                  cursor: "pointer",
                }}
              >
                {yieldIds.slice(0, 2).join(", ")}{" "}
                {yieldIds.length > 0 && (
                  <Button
                    type="link"
                    style={{ fontSize: 12, padding: 0, height: "auto" }}
                  >
                    … [Show More]
                  </Button>
                )}
              </Typography.Text>
            </Popover>
          );
        }
      },
    },
    {
      title: "Available Pre-Credits",
      dataIndex: "preCredits",
      key: "preCredits",
      render: (record) => {
        return (
          <Typography.Text style={{ fontFamily: "Lexend", fontSize: 16 }}>
            {formatNumber(record)}
          </Typography.Text>
        );
      },
    },
    {
      title: `Available ${isReductionProject ? "Inset Units" : "Credits"}`,
      dataIndex: "availableCredits",
      key: "availableCredits",
      render: (record) => {
        return (
          <Typography.Text style={{ fontFamily: "Lexend", fontSize: 16 }}>
            {formatNumber(record)}
          </Typography.Text>
        );
      },
    },
    ...(isReductionProject
      ? []
      : [
          {
            title: "Storage Duration (Years)",
            dataIndex: "storageDuration",
            key: "storageDuration",
            render: (record) => {
              return (
                <Typography.Text style={{ fontFamily: "Lexend", fontSize: 16 }}>
                  {record}
                </Typography.Text>
              );
            },
          },
        ]),
  ];

  const certificateRetirementColumns = [
    {
      title: "Retirement Date",
      dataIndex: "retiredAt",
      key: "retiredAt",
      sorter: (a, b) => (a.retiredAt ?? "").localeCompare(b.retiredAt ?? ""),
      width: 200,
      defaultSortOrder: "descend",
      render: (retiredAt) => {
        return (
          <Typography.Text style={{ fontFamily: "Lexend", fontSize: 16 }}>
            <span>
              {retiredAt
                ? moment.utc(retiredAt).local().format("DD MMM, YYYY")
                : undefined}
            </span>
          </Typography.Text>
        );
      },
    },
    {
      title: (
        <span>
          Retired Credit IDs
          <Popover
            content={
              <>
                Proba Credits are transferred and claimed in credit bundles
                (Proba Tradable Credit Bundles).
                <br />
                Each credit bundle has a specific ID.
                <br />A beneficiary has the option to claim a full bundle or a
                part of it.
              </>
            }
          >
            <span className="info-icon" style={{ marginLeft: 5 }}>
              <InfoCircleOutlined />
            </span>
          </Popover>
        </span>
      ),
      dataIndex: "tokenId",
      key: "tokenId",
      width: 250,
      render: (text, record) => {
        const tokenCount = record.amount || 0;

        if (!text || tokenCount === 0) {
          return (
            <Typography.Text style={{ fontFamily: "Lexend", fontSize: 16 }}>
              -
            </Typography.Text>
          );
        }

        const fullText = `PTCB-${text} containing ${tokenCount} credits within the range (PTCB-${text}:1 - PTCB-${text}:${tokenCount})`;

        return (
          <Popover
            content={
              <Typography.Text
                style={{
                  fontFamily: "Lexend",
                  fontSize: 14,
                  whiteSpace: "nowrap",
                }}
              >
                {fullText}
              </Typography.Text>
            }
            title="Retired Credit Details"
            trigger="click"
          >
            <Typography.Text
              style={{
                fontFamily: "Lexend",
                fontSize: 16,
                cursor: "pointer",
              }}
            >
              PTCB-{text}{" "}
              <Button
                type="link"
                style={{ fontSize: 12, padding: 0, height: "auto" }}
              >
                … [Show More]
              </Button>
            </Typography.Text>
          </Popover>
        );
      },
    },
    {
      title: "Retired Credits",
      dataIndex: "amount",
      key: "amount",
      width: 200,
      render: (record) => {
        return (
          <Typography.Text style={{ fontFamily: "Lexend", fontSize: 16 }}>
            {record}
          </Typography.Text>
        );
      },
    },
    {
      title: "Beneficiary",
      dataIndex: "beneficiaryName",
      key: "beneficiaryName",
      width: 250,
      render: (record) => {
        return (
          <div style={{ display: "flex", alignItems: "center" }}>
            <Avatar
              style={{ backgroundColor: "#87d068", marginRight: "8px" }}
              icon={<ShopOutlined />}
            />
            {record}
          </div>
        );
      },
    },
    {
      title: "Retirement Purpose",
      dataIndex: "purpose",
      key: "purpose",
      width: 300,
      render: (record) => {
        return (
          <Typography.Text style={{ fontFamily: "Lexend", fontSize: 16 }}>
            {record}
          </Typography.Text>
        );
      },
    },
    {
      title: "Period",
      dataIndex: "periods",
      width: 200,
      key: "periods",
      render: (periods) => {
        return (
          <Typography.Text style={{ fontFamily: "Lexend", fontSize: 16 }}>
            {periods.join(", ")}
          </Typography.Text>
        );
      },
    },
  ];

  const documentColumns = [
    {
      title: "Document Type",
      dataIndex: "doctype",
      key: "doctype",
      render: (record) => {
        return (
          <Typography.Text style={{ fontFamily: "Lexend", fontSize: 16 }}>
            {record}
          </Typography.Text>
        );
      },
    },
    {
      title: "Document Name",
      dataIndex: "filename",
      key: "filename",
      render: (record) => {
        return (
          <Typography.Text style={{ fontFamily: "Lexend", fontSize: 16 }}>
            {record}
          </Typography.Text>
        );
      },
    },
    {
      key: "actions",
      title: "Download",
      render: (record) => {
        return (
          <>
            <Row gutter={[10, 10]}>
              <Col>
                <DownloadOutlined
                  onClick={() => {
                    onDownloadDocument(record);
                  }}
                />
              </Col>
            </Row>
          </>
        );
      },
    },
  ];

  if (isError) {
    return <div>Error fetching data</div>;
  }

  return (
    <div>
      {!isLoading && (
        <Spin
          size="large"
          spinning={isSpinning}
          indicator={
            <LoadingOutlined style={{ fontSize: 24, color: "#36B28D" }} spin />
          }
        >
          <div style={{ height: "80vh" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <Typography.Title level={2} style={{ fontFamily: "Lexend" }}>
                {resolvedProject?.name}
              </Typography.Title>
              <div style={{ textAlign: "right" }}>
                <Button
                  type="primary"
                  onClick={handleGoBack}
                  style={{ backgroundColor: "#36B28D", color: "white" }}
                >
                  Back
                </Button>
              </div>
            </div>
            <div>
              <Typography.Paragraph
                style={{ fontSize: 16, fontFamily: "Lexend" }}
              >
                {resolvedProject?.ghg_project_summary}
              </Typography.Paragraph>
            </div>
            <div>
              <Card className="shadow-card" style={{ borderRadius: 8 }}>
                <Row gutter={[100, 20]}>
                  <Col span={12}>
                    <Statistic
                      title="Project ID"
                      value={id}
                      formatter={(value) => format(value)}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Project Status"
                      value={resolvedProject?.status}
                      formatter={(value) => format(value)}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Methodology"
                      value={methodData?.length > 0 ? "" : "Not specified"}
                      formatter={() => (
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {methodData?.length > 0
                            ? methodData?.map((item, index) => (
                                <li key={index}>{item.name}</li>
                              ))
                            : "-"}
                        </ul>
                      )}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Project Developer"
                      value={
                        <RegistryDisplayOrganizationName
                          name={resolvedProject?.organizationName}
                        />
                      }
                      formatter={(value) => format(value)}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Project Sponsor"
                      value={sponsor}
                      formatter={(value) => format(value)}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Validator"
                      value={validator}
                      formatter={(value) => format(value)}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Verifier"
                      value={verifier}
                      formatter={(value) => format(value)}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Standards Used"
                      value={resolvedProject?.standards_used}
                      formatter={(value) => format(value || "-")}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Crediting Period"
                      value={resolvedProject?.crediting_period}
                      formatter={(value) => {
                        if (!value || value.length < 2) return "Not specified";
                        const formatDate = (dateStr) =>
                          moment(new Date(dateStr)).format("MMM D, YYYY");
                        return `${formatDate(value[0])} - ${formatDate(
                          value[1]
                        )}`;
                      }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Project Period"
                      value={resolvedProject?.project_period}
                      formatter={(value) => {
                        if (!value || value.length < 2) return "Not specified";
                        const formatDate = (dateStr) =>
                          moment(new Date(dateStr)).format("MMM D, YYYY");
                        return `${formatDate(value[0])} - ${formatDate(
                          value[1]
                        )}`;
                      }}
                    />
                  </Col>
                </Row>
              </Card>
            </div>
            <div style={{ display: "flex", alignItems: "center" }}>
              <Typography.Title
                level={2}
                style={{
                  fontFamily: "Lexend",
                  marginTop: "2rem",
                  marginRight: "auto",
                }}
              >
                GHG Estimated Impact, Issued and Available{" "}
                {isReductionProject ? "Inset Units" : "Credits"}
              </Typography.Title>
            </div>
            <div>
              <Card className="shadow-card" style={{ borderRadius: 8 }}>
                <Table
                  dataSource={yieldDataList}
                  columns={YieldColumns}
                  pagination={{
                    pageSizeOptions: ["10", "50", "100"],
                    showSizeChanger: true,
                  }}
                />
              </Card>
            </div>

            <Typography.Title
              level={2}
              style={{ fontFamily: "Lexend", marginTop: "2rem" }}
            >
              Retirement Details
            </Typography.Title>
            <div>
              <Typography.Paragraph
                style={{ fontSize: 16, fontFamily: "Lexend" }}
              >
                Proba Credits are retired when a retiring entity or a
                beneficiary makes a claim. Here you can view the Proba Credits
                that have been retired for this project.
              </Typography.Paragraph>
            </div>
            <div>
              <Card className="shadow-card" style={{ borderRadius: 8 }}>
                <Table
                  dataSource={retirementList}
                  columns={certificateRetirementColumns}
                  pagination={{
                    pageSizeOptions: ["10", "50", "100"],
                    showSizeChanger: true,
                  }}
                />
              </Card>
            </div>

            <Typography.Title
              level={2}
              style={{ fontFamily: "Lexend", marginTop: "2rem" }}
            >
              Project Documents
            </Typography.Title>
            <div>
              <Card className="shadow-card" style={{ borderRadius: 8 }}>
                <Table
                  dataSource={documentList}
                  columns={documentColumns}
                  pagination={{
                    pageSizeOptions: ["10", "50", "100"],
                    showSizeChanger: true,
                  }}
                />
              </Card>
            </div>
          </div>
        </Spin>
      )}
    </div>
  );
}

export default RegistryProjectDetailsPageView;
